import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { HomeIcon, UsersIcon, AcademicCapIcon, CalendarDaysIcon, CurrencyYenIcon, ChartBarIcon, ExclamationTriangleIcon, BellIcon, ChatBubbleOvalLeftIcon, DocumentIcon, Cog6ToothIcon, ArrowRightOnRectangleIcon, XMarkIcon, Bars3Icon, BookOpenIcon, CommandLineIcon, SparklesIcon, DevicePhoneMobileIcon, KeyIcon } from '@heroicons/react/24/solid'
import { DashboardContent } from './dashboard/DashboardContent'
import { UserManagement } from './users/UserManagement'
import { CourseManagement } from './courses/CourseManagement'
import { BookingManagement } from './bookings/BookingManagement'
import { ComplaintsManagement } from './complaints/ComplaintsManagement'
import { NotificationsManagement } from './notifications/NotificationsManagement'
import { RealTimeNotifications } from './RealTimeNotifications'
import { TestPage } from './TestPage'
import { SystemSettings } from './SystemSettings'
import { PaymentManagement } from './payments/PaymentManagement'
import { ContentManagement } from './ContentManagement'
import { DataAnalysis } from './analytics/DataAnalysis'
import { MaterialsManagement } from './materials/MaterialsManagement'
import SoftwareToolsManagement from './SoftwareToolsManagement'
import MemberLevelManagement from './members/MemberLevelManagement'
import SmsConfigManagement from './sms/SmsConfigManagement'
import SmsLogsManagement from './sms/SmsLogsManagement'
import SmsManagement from './sms/SmsManagement'
import EmailManagement from './email/EmailManagement'
import toast from 'react-hot-toast'
import { authApi } from '../services/adminApi'

// 修改密码模态框组件
const ChangePasswordModal: React.FC<{ isOpen: boolean; onClose: () => void }> = ({ isOpen, onClose }) => {
  const [oldPassword, setOldPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (newPassword !== confirmPassword) {
      toast.error('两次输入的新密码不一致')
      return
    }

    if (newPassword.length < 6) {
      toast.error('新密码长度至少6位')
      return
    }

    setIsLoading(true)
    try {
      const response = await authApi.changePassword({ oldPassword, newPassword })
      if (response.success) {
        toast.success('密码修改成功')
        setOldPassword('')
        setNewPassword('')
        setConfirmPassword('')
        onClose()
      } else {
        toast.error(response.error?.message || '密码修改失败')
      }
    } catch (error: any) {
      toast.error(error.message || '密码修改失败')
    } finally {
      setIsLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />
        <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">修改密码</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">当前密码</label>
              <input
                type="password"
                value={oldPassword}
                onChange={(e) => setOldPassword(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                placeholder="请输入当前密码"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">新密码</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                placeholder="请输入新密码（至少6位）"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">确认新密码</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                placeholder="请再次输入新密码"
                required
              />
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                取消
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
              >
                {isLoading ? '保存中...' : '确认修改'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export const AdminDashboard: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [activeMenu, setActiveMenu] = useState('dashboard')
  const [showChangePassword, setShowChangePassword] = useState(false)

  // 根据URL路径设置活动菜单
  useEffect(() => {
    // 由于 main.tsx 中设置了 BrowserRouter 的 basename="/admin"
    // 所以 location.pathname 已经是不包含 /admin 的路径了
    const path = location.pathname.replace(/^\//, '')

    // 如果路径是空或者是 dashboard，设置为 dashboard
    if (!path || path === 'dashboard') {
      setActiveMenu('dashboard')
    } else {
      // 检查路径是否在菜单项中
      const isValidMenu = menuItems.some(item => item.id === path)
      if (isValidMenu) {
        setActiveMenu(path)
      } else {
        // 如果路径无效，重定向到 dashboard，但不包括初始加载的情况
        if (path !== 'login') {
          setActiveMenu('dashboard')
          navigate('/dashboard', { replace: true })
        }
      }
    }
  }, [location.pathname, navigate])

  // 菜单项
  const menuItems = [
    { id: 'dashboard', name: '仪表板', icon: HomeIcon, color: 'text-purple-600' },
    { id: 'users', name: '用户与教师管理', icon: UsersIcon, color: 'text-blue-600' },
    { id: 'bookings', name: '预约管理', icon: CalendarDaysIcon, color: 'text-green-600' },
    { id: 'courses', name: '课程管理', icon: AcademicCapIcon, color: 'text-orange-600' },
    { id: 'materials', name: '教材管理', icon: BookOpenIcon, color: 'text-teal-600' },
    { id: 'software', name: '软件工具', icon: CommandLineIcon, color: 'text-blue-600' },
    { id: 'payments', name: '支付管理', icon: CurrencyYenIcon, color: 'text-yellow-600' },
    { id: 'member-levels', name: '会员等级', icon: SparklesIcon, color: 'text-purple-600' },
    { id: 'sms-management', name: '短信管理', icon: DevicePhoneMobileIcon, color: 'text-cyan-600' },
    { id: 'email-management', name: '邮件管理', icon: ChatBubbleOvalLeftIcon, color: 'text-indigo-600' },
    { id: 'analytics', name: '数据分析', icon: ChartBarIcon, color: 'text-indigo-600' },
    { id: 'complaints', name: '投诉处理', icon: ExclamationTriangleIcon, color: 'text-red-600' },
    { id: 'notifications', name: '通知管理', icon: BellIcon, color: 'text-pink-600' },
    { id: 'test', name: '功能测试', icon: ChatBubbleOvalLeftIcon, color: 'text-yellow-600' },
    { id: 'settings', name: '系统设置', icon: Cog6ToothIcon, color: 'text-gray-600' }
  ]

  const handleLogout = () => {
    localStorage.removeItem('isAuthenticated')
    localStorage.removeItem('userType')
    localStorage.removeItem('token')
    localStorage.removeItem('refreshToken')
    localStorage.removeItem('user')
    window.location.href = '/admin/login'
  }

  // 渲染内容
  const renderContent = () => {
    switch (activeMenu) {
      case 'dashboard':
        return <DashboardContent />
      case 'users':
        return <UserManagement />
      case 'bookings':
        return <BookingManagement />
      case 'courses':
        return <CourseManagement />
      case 'materials':
        return <MaterialsManagement />
      case 'software':
        return <SoftwareToolsManagement />
      case 'payments':
        return <PaymentManagement />
      case 'member-levels':
        return <MemberLevelManagement />
      case 'sms-management':
        return <SmsManagement />
      case 'email-management':
        return <EmailManagement />
      case 'analytics':
        return <DataAnalysis />
      case 'complaints':
        return <ComplaintsManagement />
      case 'notifications':
        return <NotificationsManagement />
      case 'test':
        return <TestPage />
      case 'settings':
        return <SystemSettings />
      default:
        return (
          <div className="text-center py-12">
            <div className="max-w-md mx-auto">
              <span className="text-4xl text-gray-400">📄</span>
              <h3 className="mt-2 text-sm font-medium text-gray-900">功能开发中</h3>
              <p className="mt-1 text-sm text-gray-500">
                该功能正在开发中，敬请期待...
              </p>
            </div>
          </div>
        )
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 修改密码模态框 */}
      <ChangePasswordModal
        isOpen={showChangePassword}
        onClose={() => setShowChangePassword(false)}
      />

      {/* 侧边栏 - 桌面版 */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex min-h-0 flex-1 flex-col bg-white border-r border-gray-200">
          {/* Logo 区域 */}
          <div className="flex items-center h-16 px-6 bg-purple-600 text-white">
            <h1 className="text-xl font-bold">TalkNect</h1>
            <span className="ml-2 text-sm bg-purple-700 px-2 py-1 rounded">管理端</span>
          </div>

          {/* 导航菜单 */}
          <nav className="flex-1 px-4 py-6 space-y-2">
            {menuItems.map((item) => (
              <button
                key={item.id}
                onClick={() => {
                  setActiveMenu(item.id)
                  navigate(`/${item.id}`)
                }}
                className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors ${activeMenu === item.id
                  ? 'bg-purple-50 text-purple-700 border-l-4 border-purple-700'
                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
              >
                <item.icon className={`w-6 h-6 mr-3 ${item.color}`} />
                {item.name}
              </button>
            ))}
          </nav>

          {/* 底部退出按钮 */}
          <div className="p-4 border-t border-gray-200">
            <button
              onClick={handleLogout}
              className="w-full flex items-center px-4 py-3 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            >
              <ArrowRightOnRectangleIcon className="w-6 h-6 mr-3 text-red-600" />
              退出登录
            </button>
          </div>
        </div>
      </div>

      {/* 移动端侧边栏 */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="fixed inset-0 bg-gray-900 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
          <div className="fixed inset-y-0 left-0 flex w-64 flex-col bg-white">
            <div className="flex items-center h-16 px-6 bg-purple-600 text-white">
              <h1 className="text-xl font-bold">TalkNect</h1>
              <button
                onClick={() => setSidebarOpen(false)}
                className="ml-auto text-white hover:bg-purple-700 p-1 rounded"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
            <nav className="flex-1 px-4 py-6 space-y-2">
              {menuItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => {
                    setActiveMenu(item.id)
                    navigate(`/${item.id}`)
                    setSidebarOpen(false)
                  }}
                  className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors ${activeMenu === item.id
                    ? 'bg-purple-50 text-purple-700 border-l-4 border-purple-700'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                >
                  <item.icon className={`w-6 h-6 mr-3 ${item.color}`} />
                  {item.name}
                </button>
              ))}
            </nav>
            <div className="p-4 border-t border-gray-200">
              <button
                onClick={handleLogout}
                className="w-full flex items-center px-4 py-3 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              >
                <ArrowRightOnRectangleIcon className="w-6 h-6 mr-3 text-red-600" />
                退出登录
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 主内容区域 */}
      <div className="lg:pl-64">
        {/* 顶部导航栏 */}
        <div className="sticky top-0 z-40 bg-white border-b border-gray-200">
          <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden text-gray-500 hover:text-gray-700"
            >
              <Bars3Icon className="w-6 h-6" />
            </button>
            <div className="flex items-center space-x-4">
              {((import.meta as any).env?.VITE_NOTIFICATIONS_ENABLED === 'true') && <RealTimeNotifications />}
              <div className="relative">
                <button className="p-2 text-gray-500 hover:text-gray-700">
                  <ChatBubbleOvalLeftIcon className="w-6 h-6" />
                  <span className="absolute top-0 right-0 block h-2 w-2 rounded-full bg-red-400"></span>
                </button>
              </div>
              {/* 修改密码按钮 */}
              <button
                onClick={() => setShowChangePassword(true)}
                className="p-2 text-purple-600 hover:text-purple-800 hover:bg-purple-50 rounded-lg transition-colors"
                title="修改密码"
              >
                <KeyIcon className="w-5 h-5" />
              </button>
              <div className="flex items-center space-x-3">
                <div className="text-right">
                  <p className="text-sm font-medium text-gray-900">管理员</p>
                  <p className="text-xs text-gray-500">在线</p>
                </div>
                <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center">
                  <span className="text-white text-sm font-medium">A</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 页面内容 */}
        <div className="p-4 sm:p-6 lg:p-8">
          {renderContent()}
        </div>
      </div>
    </div>
  )
}

